/** Lab member Account Statement — Betting P&L by market (getDownlineAccountsForMember). */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Banner,
  Button,
  Card,
  DataTable,
  DEFAULT_PAGE_SIZE,
  Field,
  IconButton,
  Input,
  Legend,
  Pager,
  Segmented,
  Select,
  type Column,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { Money } from '@/app/bits'
import { useGames } from '@/api/hooks/reports/useGames'
import { useSubGames } from '@/api/hooks/reports/useSubGames'
import { lll, money } from '@/lib/format'
import type { PlStatementRowView } from '@/api/map/plStatement'
import { PlStatementDetailDrawer } from '@/features/reports/PlStatementDetailDrawer'
import type { Role } from '@/features/hierarchy/hierarchy'
import { isActivityRemovedFilterName } from '@/api/map/memberActivityMatrix'
import {
  isoDatetimeOffset,
  memberStatementAmount,
  memberStatementDescription,
  memberStatementKindLabel,
  memberStatementEventLabel,
  memberStatementStatus,
  memberStatementTotalNetShare,
  isStatementTransferSubGame,
  statementRangeLabel,
  toMemberStatementRow,
  type MemberStatementMode,
  type MemberStatementRow,
} from '@/features/member/memberStatement'

export function MemberStatementTab({
  memberId,
  username,
  viewedRole = 'player',
}: {
  memberId: string
  /** Lab / Agent card subtitle: `boplayer1 · from → to`. */
  username?: string
  /** Lab `$stateParams.level` of the member being viewed. */
  viewedRole?: Role
}) {
  // Lab / Agent: from = 6d ago 00:00 · to = today 23:59 (datetime-local).
  const [fromDate, setFromDate] = useState(() => isoDatetimeOffset(6))
  const [toDate, setToDate] = useState(() => isoDatetimeOffset(0, true))
  const [gameId, setGameId] = useState('All')
  const [subGame, setSubGame] = useState('All')
  const [mode, setMode] = useState<MemberStatementMode>('player')
  const [applied, setApplied] = useState({
    from: isoDatetimeOffset(6),
    to: isoDatetimeOffset(0, true),
    game: 'All',
    subGame: 'All',
  })
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<PlStatementRowView | null>(null)
  const pageSize = DEFAULT_PAGE_SIZE

  const games = useGames()
  const subGames = useSubGames(gameId === 'All' ? null : gameId)

  const gameOptions = useMemo(() => {
    const rows = games.data ?? []
    return [
      { value: 'All', label: 'All' },
      ...rows
        .filter((g) => !isActivityRemovedFilterName(g.name))
        .map((g) => ({ value: g._id, label: g.name })),
    ]
  }, [games.data])

  const subGameOptions = useMemo(() => {
    const names = subGames.data ?? []
    return [
      { value: 'All', label: 'All' },
      ...names
        .filter((name) => !isActivityRemovedFilterName(name))
        .map((name) => ({ value: name, label: name })),
    ]
  }, [subGames.data])

  const statement = useQuery({
    queryKey: ['cm', 'member', 'account-statement', memberId, applied, page, pageSize],
    queryFn: async () => {
      const body: Record<string, unknown> = {
        memberId,
        fromDate: applied.from,
        toDate: applied.to,
        page,
        pageSize,
      }
      if (applied.game && applied.game !== 'All') body.game = applied.game
      if (applied.subGame && applied.subGame !== 'All') body.subGame = applied.subGame
      const res = await cmApi.getDownlineAccountsForMember(authHeaders(), body)
      const rows = (res.data?.result ?? []).map(toMemberStatementRow)
      return {
        rows,
        total: res.data?.totalCount ?? rows.length,
        maxRow: res.data?.maxRow ?? pageSize,
        net: typeof res.data?.net === 'number' ? res.data.net : null,
      }
    },
    enabled: Boolean(memberId),
  })

  const rows = statement.data?.rows ?? []
  const total = statement.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))

  const totalNetShare = useMemo(() => {
    // API `net` is SUM(player net_amount) across all pages — Player P&L mode.
    if (mode === 'player' && typeof statement.data?.net === 'number') {
      return statement.data.net
    }
    return memberStatementTotalNetShare(rows, mode, viewedRole)
  }, [statement.data?.net, rows, mode, viewedRole])

  const moneyHeader = mode === 'player' ? 'Player P&L' : 'P&L'

  const columns = useMemo<Column<MemberStatementRow>[]>(
    () => [
      {
        id: 'when',
        header: 'Date',
        cell: (row) => lll(row.settledAt),
        sortValue: (row) => row.settledAt,
      },
      {
        id: 'kind',
        header: 'Description',
        cell: (row) => memberStatementKindLabel(row),
        sortValue: (row) => memberStatementKindLabel(row),
      },
      {
        id: 'event',
        header: 'Event',
        cell: (row) => (
          <button
            type="button"
            className="w7-link"
            onClick={() => setSelected(row)}
            title={memberStatementEventLabel(row)}
          >
            {memberStatementDescription(row)}
          </button>
        ),
        sortValue: (row) => memberStatementDescription(row),
      },
      {
        id: 'pnl',
        header: moneyHeader,
        numeric: true,
        cell: (row) => (
          <Money
            value={memberStatementAmount(row, mode, viewedRole)}
            currency={row.currencyType}
            signed
          />
        ),
        sortValue: (row) => memberStatementAmount(row, mode, viewedRole),
      },
      {
        id: 'prevBal',
        header: 'Previous balance',
        numeric: true,
        cell: (row) =>
          row.previousBalance != null
            ? money(row.previousBalance, {
                currency: row.currencyType,
                alreadyDisplay: true,
              })
            : '—',
        sortValue: (row) => row.previousBalance ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'nextBal',
        header: 'Next balance',
        numeric: true,
        cell: (row) =>
          row.nextBalance != null
            ? money(row.nextBalance, {
                currency: row.currencyType,
                alreadyDisplay: true,
              })
            : '—',
        sortValue: (row) => row.nextBalance ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'expo',
        header: 'Exposure',
        numeric: true,
        cell: (row) =>
          row.exposure != null
            ? money(row.exposure, {
                currency: row.currencyType,
                alreadyDisplay: true,
              })
            : '—',
        sortValue: (row) => row.exposure ?? Number.NEGATIVE_INFINITY,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => {
          const status = memberStatementStatus(row)
          if (status === 'Rollback' || status === 'P&L Rollback') {
            return <span style={{ color: 'orange' }}>{status}</span>
          }
          if (status === 'Invalid' || status === 'Invalidate P&L') {
            return <span className="w7-loss">{status}</span>
          }
          return status
        },
        sortValue: (row) => memberStatementStatus(row),
      },
      {
        id: 'detail',
        header: 'Details',
        width: 72,
        cell: (row) =>
          isStatementTransferSubGame(row.subGame) ? (
            '—'
          ) : (
            <IconButton
              name="eye"
              size="sm"
              label="Game Detail"
              onClick={() => setSelected(row)}
            />
          ),
      },
    ],
    [mode, viewedRole, moneyHeader],
  )

  const runSearch = () => {
    setPage(1)
    setApplied({
      from: fromDate,
      to: toDate,
      game: gameId,
      subGame,
    })
  }

  return (
    <div className="cm-member-statement w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Account Statement filters">
        <div className="w7-transfer-filter-row" style={{ flexWrap: 'wrap', gap: 'var(--w7-s3)' }}>
          <Field label="Select Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Games"
                value={gameId}
                onChange={(e) => {
                  setGameId(e.target.value)
                  setSubGame('All')
                }}
                options={gameOptions}
                disabled={games.isLoading}
              />
            )}
          </Field>
          <Field label="Select Sub Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Sub Games"
                value={subGame}
                onChange={(e) => setSubGame(e.target.value)}
                options={subGameOptions}
                disabled={subGames.isLoading || gameId === 'All'}
              />
            )}
          </Field>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input
                id={id}
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            )}
          </Field>
          <Button type="button" variant="primary" busy={statement.isFetching} onClick={runSearch}>
            Search
          </Button>
        </div>
      </div>

      <div
        className="w7-row"
        style={{
          flexWrap: 'wrap',
          gap: 'var(--w7-s3)',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Segmented
          label="Account Statement mode"
          value={mode}
          onChange={(v) => setMode(v as MemberStatementMode)}
          items={[
            { id: 'player', label: 'Player P&L Statement' },
            { id: 'total', label: 'P&L Statement' },
          ]}
        />
        <div className="w7-row" style={{ gap: 'var(--w7-s3)', alignItems: 'center' }}>
          <span className="w7-dim">
            Total Net Share:{' '}
            <Money value={totalNetShare} signed />
          </span>
          <Legend
            items={[
              { label: 'Player Loss', color: 'var(--w7-loss)' },
              { label: 'Player Win', color: 'var(--w7-profit)' },
            ]}
          />
        </div>
      </div>

      {statement.isError ? (
        <Banner tone="error" title="Statement could not be loaded">
          {(statement.error as Error)?.message}
        </Banner>
      ) : null}

      <Card
        flush
        title="Account Statement"
        subtitle={
          username
            ? `${username} · ${statementRangeLabel(applied.from, applied.to)}`
            : statementRangeLabel(applied.from, applied.to)
        }
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="Account statement"
          rowKey={(row) => row.id}
          loading={statement.isLoading}
          rows={rows}
          columns={columns}
          empty={<p className="cm-hint">No settled markets in this range.</p>}
        />
      </Card>

      {selected ? (
        <PlStatementDetailDrawer
          row={selected}
          currency={selected.currencyType}
          onClose={() => setSelected(null)}
        />
      ) : null}
    </div>
  )
}
